"use client";

import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Loader2, X } from "lucide-react";
import { TALK_NOTE_TEMPLATES, type TalkNoteTemplate } from "./templates";

interface TemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  onPick: (tpl: TalkNoteTemplate) => void;
  creating?: boolean;
}

export default function TemplatePickerModal({
  open,
  onClose,
  onPick,
  creating,
}: TemplatePickerModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--background)] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between gap-4 border-b border-[var(--border)] px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold text-[var(--foreground)]">
              {t("Choose a template")}
            </h2>
            <p className="mt-0.5 text-[13.5px] text-[var(--muted-foreground)]">
              {t("Start your draft from a ready-made structure.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)]"
            aria-label={t("Close")}
          >
            <X size={18} />
          </button>
        </header>

        <div className="grid grid-cols-1 gap-3 overflow-y-auto p-5 sm:grid-cols-2">
          {TALK_NOTE_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              type="button"
              disabled={creating}
              onClick={() => onPick(tpl)}
              className="group flex flex-col items-start rounded-xl border border-[var(--border)] p-4 text-left transition-colors hover:border-[var(--ring)] hover:bg-[var(--muted)] disabled:opacity-60"
            >
              <div className="flex items-center gap-2">
                <FileText
                  size={15}
                  className="shrink-0 text-[var(--muted-foreground)]"
                />
                <span className="text-[13.5px] font-medium text-[var(--foreground)]">
                  {t(tpl.title)}
                </span>
              </div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[var(--muted-foreground)]">
                {t(tpl.description)}
              </p>
            </button>
          ))}
        </div>

        {creating ? (
          <div className="flex items-center justify-center gap-2 border-t border-[var(--border)] py-3 text-[13.5px] text-[var(--muted-foreground)]">
            <Loader2 size={14} className="animate-spin" />
            {t("Creating…")}
          </div>
        ) : null}
      </div>
    </div>
  );
}
